import { useCallback, useContext, useMemo } from 'react';
import { ChartGroupByContext, ChartInfoContext, ChartTypeContext } from './context';
import type { BarChart, MultipleBarChart, MultipleLineChart, PieChart, LineChart } from '@trinity/chat';
import { getGroupedBarChartData, getGroupedPieChartData } from './Message.Response.Chart.utils';
import { useOptionsColors } from '@trinity/chat-components/containers/AppTheming/useOptionsColors';
import { NivoBarChart } from '@trinity/chat-components/components/Chat.Charts/Bar.js';
import { NivoLineChart } from '@trinity/chat-components/components/Chat.Charts/Line.js';
import { NivoPieChart } from '@trinity/chat-components/components/Chat.Charts/Pie.js';
import { NivoMultipleBarChart } from '../Chat.Charts/MultipleBar';
import { NivoStackedBarChart } from '@trinity/chat-components/components/Chat.Charts/StackedBar.js';
import { NivoDoughnutChart } from '@trinity/chat-components/components/Chat.Charts/Doughnut.js';

type LegendItem = {
  key: string;
  color: string;
};

export const useResponseChartLegend = () => {
  const [chart] = useContext(ChartInfoContext);
  const [chartType] = useContext(ChartTypeContext);
  const [groupBy] = useContext(ChartGroupByContext);
  const colors = useOptionsColors();

  const legend = useMemo(() => {
    const legendItems: Array<LegendItem> = [];
    const chartData = chart?.chart?.chart;

    if (!chartData) return legendItems;

    switch (chartType) {
      case 'stacked-bar':
      case 'bar': {
        const data = getGroupedBarChartData(chartData.data as BarChart['data'], groupBy);
        for (let i = 0; i < data.length; i++) {
          legendItems.push({ key: data[i].x, color: colors[i] });
        }
        break;
      }

      case 'pie': {
        const data = getGroupedPieChartData(chartData.data as PieChart['data'], groupBy);
        for (let i = 0; i < data.length; i++) {
          legendItems.push({ key: data[i].label, color: colors[i] });
        }
        break;
      }

      case 'multiple-stacked-bar':
      case 'multiple-clustered-bar': {
        const multipleChart = chartData as MultipleBarChart;
        const keys = new Set<string>();
        for (let i = 0; i < multipleChart.data.length; i++) {
          for (let j = 0; j < multipleChart.data[i].data.length; j++) {
            keys.add(multipleChart.data[i].data[j].x);
          }
        }
        const keysArray = [...keys];
        for (let i = 0; i < keysArray.length; i++) {
          legendItems.push({ key: keysArray[i], color: colors[i] });
        }
        break;
      }

      case 'multiple-line': {
        const lineChart = chartData as MultipleLineChart;
        const keys = new Set<string>();
        for (let i = 0; i < lineChart.data.length; i++) {
          keys.add(lineChart.data[i].id);
        }
        const keysArray = [...keys];
        for (let i = 0; i < keysArray.length; i++) {
          legendItems.push({ key: keysArray[i], color: colors[i] });
        }
        break;
      }

      case 'doughnut': {
        const pieChart = chartData as PieChart;
        const data = getGroupedPieChartData(pieChart.data, groupBy);
        for (let i = 0; i < data.length; i++) {
          legendItems.push({ key: data[i].label, color: colors[i] });
        }
      }
    }

    return legendItems;
  }, [chart, chartType, groupBy, colors]);

  return legend;
};

type RenderResponseChart = {
  legend: LegendItem[];
  unitInfo?: {
    unit: string;
    isPrefix: boolean;
  };
};

export const useRenderResponseChart = (props: RenderResponseChart) => {
  const { legend, unitInfo } = props;
  const [chart] = useContext(ChartInfoContext);
  const [groupBy] = useContext(ChartGroupByContext);
  const colors = useOptionsColors();

  const renderChart = useCallback((chartType: string) => {
    const chartData = chart.chart.chart;

    switch (chartType) {
      case 'stacked-bar': {
        const data = getGroupedBarChartData(chartData.data as BarChart['data'], groupBy);
        const chartColors = [];
        for (let i = 0; i < data.length; i++) {
          chartColors.push(colors[i]);
        }
        return <NivoStackedBarChart chart={{ ...chartData, data, indexBy: chartData.xAxisTitle }} colors={chartColors} unit={unitInfo?.unit} isPrefix={unitInfo?.isPrefix} />;
      }

      case 'bar': {
        const data = getGroupedBarChartData(chartData.data as BarChart['data'], groupBy);
        const chartColors = [];
        for (let i = 0; i < data.length; i++) {
          chartColors.push(colors[i]);
        }
        return <NivoBarChart chart={{ ...chartData, data, indexBy: chartData.xAxisTitle }} colors={chartColors} unit={unitInfo?.unit} isPrefix={unitInfo?.isPrefix} />;
      }

      case 'pie': {
        const data = getGroupedPieChartData(chartData.data as PieChart['data'], groupBy);
        const chartColors = [];
        for (let i = 0; i < data.length; i++) {
          chartColors.push(colors[i]);
        }
        return <NivoPieChart chart={{ ...chartData, data }} colors={chartColors} unit={unitInfo?.unit} isPrefix={unitInfo?.isPrefix} />;
      }
      case 'line': {
        const chart = chartData as LineChart;
        return <NivoLineChart chart={chart} colors={colors} />;
      }

      case 'doughnut': {
        const chart = chartData as PieChart;
        const data = getGroupedPieChartData(chart.data, groupBy);
        for (let i = 0; i < data.length; i++) {
          legend.push({ key: data[i].label, color: colors[i] });
        }
        return <NivoDoughnutChart chart={{ ...chart, data }} colors={colors} />;
      }

      case 'multiple-stacked-bar': {
        const chart = chartData as MultipleBarChart;
        return (
          <NivoMultipleBarChart
            chart={chart}
            colors={colors}
            isStacked={true}
            unit={unitInfo?.unit}
            isPrefix={unitInfo?.isPrefix} />
        );
      }

      case 'multiple-clustered-bar': {
        const chart = chartData as MultipleBarChart;
        return (
          <NivoMultipleBarChart
            chart={chart}
            colors={colors}
            isStacked={false}
            unit={unitInfo?.unit}
            isPrefix={unitInfo?.isPrefix} />
        );
      }

      case 'multiple-line': {
        const chart = chartData as MultipleLineChart;
        return <NivoLineChart chart={chart} colors={colors} />;
      }

      default:
        return null;
    }
  }, [chart, groupBy, colors, legend]);

  return renderChart;
};